<template>
    <div class="mx-4">
        <v-overlay :value="isLoading">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <pure-datatable endpoint="/api/financial/rappel" :queryParams="queryParams" ref="datatable" @data-changed="onDataChanged" v-slot:default="{ vOn, vBind, isLoading }">
            <v-data-table
                class="elevation-1 d-flex flex-column justify-space-between no-extra-padding-data-table datatable-paid-rappel"
                :class="{ 'nowrap-headers': $vuetify.breakpoint.width >= 1600 }"
                :style="`border-radius: 16px; height: ${datatableSize}px;`"
                fixed-header
                multi-sort
                :headers="tHeaders(headers)"
                :header-props="headerProps"
                @update:options="refresh"
                v-on="vOn"
                v-bind="vBind"
                hide-default-footer
                :no-data-text="$t('table.noData')"
                :no-results-text="$t('table.noResults')"
                :loading="false"
                :loading-text="$t('table.loading')"
                :footer-props="{
                    showFirstLastPage: false,
                    itemsPerPageOptions: [10, 20, 50],
                    itemsPerPageText: $t('table.itemsPerPage'),
                }"
            >
                <template #[`item.document_date`]="{ item }">
                    <div class="d-flex justify-start align-center">
                        {{ formatDate(item.document_date) }}
                    </div>
                </template>

                <template #[`item.document_number`]="{ item }">
                    <div class="d-flex justify-center align-center">
                        {{ item.document_number }}
                    </div>
                </template>

                <template #[`item.designation`]="{ item }">
                    <div class="d-flex justify-start align-center">
                        <div class="datatable-designation-text">
                            {{ item.designation }}
                        </div>
                    </div>
                </template>

                <template #[`item.total`]="{ item }">
                    <div class="d-flex justify-center align-center">
                        {{ formatPrice(item.total) }}
                    </div>
                </template>

                <template #[`item.rappel`]="{ item }">
                    <div class="d-flex justify-center align-center">
                        {{ formatPrice(item.rappel) }}
                    </div>
                </template>

                <template #[`item.actions`]="{ item }">
                    <div class="d-flex justify-end align-center">
                        <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="hasInvoiceAttached(item)">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="downloadInvoice(item)">mdi-download</v-icon>
                            </template>

                            <span>{{ tb('Descarregar documento') }}</span>
                        </v-tooltip>
                    </div>
                </template>

                <template v-slot:footer="scope">
                    <div>
                        <div class="datatable-footer-border"></div>

                        <data-table-footer :showExport="canExport()" :scope="scope" @export="exportData" @updateOptions="refresh" :showOrderButton="false" :isLoading="isLoading" style="height: fit-content"> </data-table-footer>
                    </div>
                </template>
            </v-data-table>
        </pure-datatable>
    </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useUserStore } from '@/Stores/user';
import { useBranchStore } from '@/Stores/branch';
import { useAppStore } from '@/Stores/app';
import PureDatatable from '@arentia/atools/PureDatatable';
import { axios } from '@/Services/Backend';
import moment from 'moment';

export default {
    components: {
        PureDatatable,
    },

    props: {
        queryParams: {
            type: Object,
            required: true,
        },

        schema: {
            type: Object,
            required: true,
        },

        filters: {
            type: Object,
            required: true,
        },

        headers: {
            type: Array,
            required: true,
        },

        selectedPartition: {
            type: Array,
            required: true,
        },

        contract: {
            type: Object,
        },
    },

    data: () => ({
        isLoading: false,
        datatableSize: 400,
        hasExpandedActiveFilters: false,

        selectedRow: {},
        downloadInvoiceDone: false,
    }),

    mounted() {
        // @Note: Every time we change from 'CalculatedRappel' to 'PaidRappel'
        //         we'll want a refresh to happen as the dates might have changed.
        this.refresh();
    },

    watch: {
        'branchStore.branch': {
            handler: function () {
                this.refresh();
            },
        },

        filters: {
            handler: function () {
                this.refresh();
            },
            deep: true,
        },

        selectedPartition: {
            handler: function () {
                this.refresh();
            },
            deep: true,
        },

        contract: {
            handler: function () {
                this.refresh();
            },
        },

        downloadInvoiceDone: {
            handler: function (value) {
                if (value) {
                    this.downloadInvoiceDone = false;
                    this.downloadInvoice(this.selectedRow, true);
                }
            },
            immediate: true,
        },
    },

    computed: {
        ...mapStores(useUserStore, useBranchStore, useAppStore),

        isMobile(){
            return this.appStore.mobile_ls?false:this.appStore.is_mobile;
        },
        headerProps(){
            return this.appStore.header_props
        }
    },

    methods: {
        onDataChanged() {
            const wrapper = this.$refs['datatable']?.$el?.querySelector('div.v-data-table__wrapper');
            this.scrollDatatableToTop(this.$vuetify, wrapper);
        },

        refresh(options) {
            const localFilters = JSON.parse(JSON.stringify(this.filters));
            if (this.filters.start_date) localFilters.start_date = moment(this.filters.start_date).format('YYYY-MM-DD');
            if (this.filters.end_date) localFilters.end_date = moment(this.filters.end_date).endOf('month').format('YYYY-MM-DD');

            if (this.$refs.datatable) {
                if (options) {
                    this.$refs.datatable.setLocalOptions(options);
                }

                this.$refs.datatable.onChangeQueryBuilder(localFilters, this.schema);
            }
        },

        canExport() {
            return this.userStore.hasPermissionTo('View rappel');
        },

        exportData() {
            if (!this.branchStore.branch || !this.$refs.datatable) return;

            const axiosSettings = {
                url: '/api/financial/rappel/documents/export',
                params: {
                    client_number: this.branchStore.branch.client_number,
                },
            };

            this.$refs.datatable.exportData(axiosSettings, 'export_rappel_pago');
        },

        hasInvoiceAttached(item) {
            return !item.document_number.startsWith('RE');
        },

        downloadInvoice(item, is_psycho = false) {
            this.isLoading = true;

            this.selectedRow = item;
            const url = '/api/financial/rappel/invoice';

            axios({
                url,
                responseType: 'blob',
                params: {
                    branch_id: this.branchStore.branch.id,
                    document_id: item.document_number,
                    is_psycho: is_psycho,
                },
            })
                .then((response) => {
                    let fileURL = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    let fileLink = document.createElement('a');

                    fileLink.href = fileURL;
                    fileLink.download = !is_psycho ? item.document_number : item.document_number + '-psico';

                    fileLink.click();
                })
                .catch(() => {
                    if (!is_psycho) this.showError(this.tb('Não foi possível descarregar o documento.'));
                })
                .finally(() => {
                    if (!is_psycho) this.downloadInvoiceDone = true;
                    this.isLoading = false;
                });
        },
    },
};
</script>
